import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MusicNotes, Check, Sparkle } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_LABELS = {
  hindu: { title: 'Hindu Weddings', accent: 'border-amber-400 bg-amber-50/40' },
  christian: { title: 'Christian Weddings', accent: 'border-sky-400 bg-sky-50/40' },
  muslim: { title: 'Muslim Weddings', accent: 'border-emerald-400 bg-emerald-50/40' },
};

/**
 * Pro Dashboard card — only rendered when the professional's category is
 * `nathaswaram_music`. Pulls the 18-item catalog, lets the vendor tick which
 * services they offer + set a price per service, and persists via
 * `PUT /api/celebrate-music/me`.
 *
 * Universal items (Chenda Melam, Live Band / Orchestra) get a small badge
 * because they surface across every event flow, not just their tradition.
 */
const CelebrateMusicSelector = () => {
  const [catalog, setCatalog] = useState([]);
  const [picks, setPicks] = useState({});   // { key: { checked, price } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [catRes, meRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/celebrate-music/catalog`),
          axios.get(`${BACKEND_URL}/api/celebrate-music/me`, { withCredentials: true }),
        ]);
        const cat = catRes.data?.music_types || [];
        setCatalog(cat);
        const existing = {};
        for (const s of (meRes.data?.services || [])) {
          existing[s.key] = { checked: true, price: String(s.price || '') };
        }
        // ensure every key has a row so toggling is immediate
        for (const m of cat) {
          if (!existing[m.key]) existing[m.key] = { checked: false, price: '' };
        }
        setPicks(existing);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load music catalog');
      }
      setLoading(false);
    };
    load();
  }, []);

  const togglePick = (key) => {
    setPicks(prev => ({ ...prev, [key]: { ...prev[key], checked: !prev[key]?.checked } }));
  };
  const setPrice = (key, val) => {
    setPicks(prev => ({ ...prev, [key]: { ...prev[key], price: val } }));
  };

  const handleSave = async () => {
    // validate: every checked item needs a price > 0
    const services = [];
    for (const m of catalog) {
      const p = picks[m.key];
      if (!p?.checked) continue;
      const n = Number(p.price);
      if (!n || n < 1) {
        toast.error(`Enter a price for "${m.label}" or untick it`);
        return;
      }
      services.push({ key: m.key, price: n });
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/celebrate-music/me`,
        { services }, { withCredentials: true });
      toast.success(`Saved ${services.length} music service${services.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="music-selector-loading">
        <CardContent className="py-6 text-sm text-text-secondary text-center">Loading music catalog…</CardContent>
      </Card>
    );
  }

  const groups = ['hindu', 'christian', 'muslim'].map(trad => ({
    tradition: trad,
    items: catalog.filter(m => m.tradition === trad),
  }));

  const totalChecked = catalog.reduce((acc, m) => acc + (picks[m.key]?.checked ? 1 : 0), 0);

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="celebrate-music-selector">
      <CardHeader className="border-b border-[#E8E1D5]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-2xl flex items-center gap-2">
              <MusicNotes size={22} className="text-brand-primary" /> Celebrate With Music — Your Services
            </CardTitle>
            <CardDescription>
              Tick every music service you can perform and set your price. Customers will see you under each wedding tradition you've ticked. Items marked <Sparkle size={12} className="inline" /> show on every event (all weddings + non-wedding events).
            </CardDescription>
          </div>
          <Button onClick={handleSave} disabled={saving}
            className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
            data-testid="music-save-btn">
            {saving ? 'Saving…' : `Save (${totalChecked} ticked)`}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-5 space-y-4">
        {groups.map(g => (
          <div key={g.tradition}
            className={`border-2 rounded p-3 ${GROUP_LABELS[g.tradition].accent}`}
            data-testid={`music-group-${g.tradition}`}>
            <div className="text-sm font-bold text-text-primary mb-3">{GROUP_LABELS[g.tradition].title}</div>
            <div className="space-y-2">
              {g.items.map(m => {
                const p = picks[m.key] || { checked: false, price: '' };
                return (
                  <div key={m.key}
                    className={`flex items-center gap-3 p-2 border-2 rounded bg-white transition-all ${p.checked ? 'border-brand-primary' : 'border-[#E8E1D5]'}`}
                    data-testid={`music-row-${m.key}`}>
                    <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                      <input type="checkbox"
                        checked={p.checked}
                        onChange={() => togglePick(m.key)}
                        className="h-4 w-4 accent-brand-primary flex-shrink-0"
                        data-testid={`music-check-${m.key}`} />
                      <span className="text-sm font-medium truncate">{m.label}</span>
                      {m.universal && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-brand-primary text-sand-50 font-bold uppercase tracking-wider rounded flex-shrink-0">
                          All Events
                        </span>
                      )}
                    </label>
                    {p.checked && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-text-secondary">₹</span>
                        <Input
                          type="number" min={1}
                          value={p.price}
                          onChange={(e) => setPrice(m.key, e.target.value)}
                          placeholder="Price"
                          className="rounded-none h-8 w-24 text-sm"
                          data-testid={`music-price-${m.key}`} />
                      </div>
                    )}
                    {p.checked && Number(p.price) > 0 && (
                      <Check size={14} className="text-green-600 flex-shrink-0" weight="bold" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default CelebrateMusicSelector;
